import { Building2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ZAKAZKA_STAVY } from '@helfr/domain';
import { api, hlaskaZChyby } from '../api/client.js';
import type { Uzivatel, Zakazka } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { ZAKAZKA_STAV_BARVA, ZAKAZKA_STAV_LABEL } from '../lib/labels.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import {
  Badge,
  Btn,
  Chyba,
  EmptyState,
  Input,
  Karta,
  Pole,
  PoleKarta,
  Select,
  SkeletonSeznam,
  td,
  th,
} from '../components/ui.js';

/**
 * Seznam uživatelů pro sloupec „odpovědný" a výběr odpovědného. Endpoint
 * /api/users vyžaduje admin.users — bez něj tiše degradujeme (jen ID nejde
 * přeložit na jméno, zobrazí se „—").
 */
export function useUzivateleProVyber(povoleno: boolean) {
  return useQuery({
    queryKey: ['uzivatele'],
    queryFn: () => api.get<Uzivatel[]>('/api/users'),
    enabled: povoleno,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function jmenoUzivatele(id: string | null | undefined, uzivatele?: Uzivatel[]): string {
  if (!id) return '—';
  return uzivatele?.find((u) => u.id === id)?.jmeno ?? '—';
}

/** Seznam zakázek (číslo, název, stav, odpovědný) + založení nové zakázky. */
export function ZakazkyPage() {
  const { maPravo } = useAuth();
  const navigate = useNavigate();
  const [zakladam, setZakladam] = useState(false);

  const {
    data: zakazky,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  const { data: uzivatele } = useUzivateleProVyber(maPravo('admin.users'));
  // Vybraná (aktivní) zakázka — klik na řádek ji nastaví jako kontext pro celou appku.
  const { vybranaId, nastav } = useVybranaZakazka(zakazky);

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Zakázky</h1>
          <p className="text-base text-text-secondary">
            Klikněte na zakázku — nastaví se jako aktivní pro celou appku (deník, výkresy, změny…).
          </p>
        </div>
        {maPravo('projekty.write') && !zakladam && (
          <Btn onClick={() => setZakladam(true)}>Nová zakázka</Btn>
        )}
      </div>

      {zakladam && <NovaZakazkaForm uzivatele={uzivatele} onHotovo={() => setZakladam(false)} />}

      <Karta>
        {isLoading && <SkeletonSeznam radku={4} />}
        {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
        {zakazky && zakazky.length === 0 && (
          <EmptyState
            ikona={<Building2 size={22} strokeWidth={1.75} />}
            titulek="Zatím žádné zakázky"
            popis="Začněte tlačítkem „Nová zakázka“ — od ní se odvíjí deník, výkresy i změny."
          />
        )}
        {zakazky && zakazky.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Číslo</th>
                  <th className={th}>Název</th>
                  <th className={th}>Stav</th>
                  {uzivatele && <th className={th}>Odpovědný</th>}
                  <th className={th}>Objekty</th>
                </tr>
              </thead>
              <tbody>
                {zakazky.map((z) => (
                  <tr
                    key={z.id}
                    className={`cursor-pointer ${
                      z.id === vybranaId ? 'bg-primary-tint' : 'hover:bg-bg'
                    }`}
                    onClick={() => {
                      nastav(z.id);
                      void navigate(`/zakazky/${z.id}`);
                    }}
                  >
                    <td className={td}>{z.cislo}</td>
                    <td className={td}>
                      <span className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/zakazky/${z.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            nastav(z.id);
                          }}
                          className="font-semibold text-primary underline underline-offset-2"
                        >
                          {z.nazev}
                        </Link>
                        {z.id === vybranaId && <Badge stav="primary">pracujete zde</Badge>}
                      </span>
                    </td>
                    <td className={td}>
                      <Badge barva={ZAKAZKA_STAV_BARVA[z.stav]}>{ZAKAZKA_STAV_LABEL[z.stav]}</Badge>
                    </td>
                    {uzivatele && (
                      <td className={td}>{jmenoUzivatele(z.odpovednyUserId, uzivatele)}</td>
                    )}
                    <td className={td}>{z.objekty?.length ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karta>
    </>
  );
}

function NovaZakazkaForm({
  uzivatele,
  onHotovo,
}: {
  uzivatele?: Uzivatel[];
  onHotovo: () => void;
}) {
  const queryClient = useQueryClient();
  const [cislo, setCislo] = useState('');
  const [nazev, setNazev] = useState('');
  const [objednatel, setObjednatel] = useState('');
  const [lokalita, setLokalita] = useState('');
  const [stav, setStav] = useState<string>('priprava');
  const [odpovednyUserId, setOdpovednyUserId] = useState('');

  const zalozit = useMutation({
    mutationFn: () =>
      api.post<Zakazka>('/api/projects', {
        cislo,
        nazev,
        objednatel: objednatel || null,
        lokalita: lokalita || null,
        stav,
        odpovednyUserId: odpovednyUserId || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zakazky'] });
      onHotovo();
    },
  });

  const odeslat = (e: FormEvent) => {
    e.preventDefault();
    zalozit.mutate();
  };

  // Jeden sloupec + logické pořadí (identita → detail); šířka pole ~ délka
  // vstupu (číslo/stav krátké); popisky nad polem; nepovinná bez hvězdičky
  // (formulářová pravidla, podklady/18).
  return (
    <PoleKarta ikona={<Building2 size={16} strokeWidth={1.75} />} nadpis="Nová zakázka">
      <form onSubmit={odeslat} className="flex max-w-xl flex-col gap-4">
        <Pole popisek="Číslo zakázky" povinne>
          <Input
            className="max-w-44"
            placeholder="2026-001"
            required
            value={cislo}
            onChange={(e) => setCislo(e.target.value)}
          />
        </Pole>
        <Pole popisek="Název" povinne>
          <Input
            placeholder="např. Hala Kladno"
            required
            value={nazev}
            onChange={(e) => setNazev(e.target.value)}
          />
        </Pole>
        <Pole popisek="Objednatel">
          <Input value={objednatel} onChange={(e) => setObjednatel(e.target.value)} />
        </Pole>
        <Pole popisek="Lokalita">
          <Input value={lokalita} onChange={(e) => setLokalita(e.target.value)} />
        </Pole>
        <Pole popisek="Stav">
          <Select className="max-w-60" value={stav} onChange={(e) => setStav(e.target.value)}>
            {ZAKAZKA_STAVY.map((s) => (
              <option key={s} value={s}>
                {ZAKAZKA_STAV_LABEL[s]}
              </option>
            ))}
          </Select>
        </Pole>
        {uzivatele && (
          <Pole popisek="Odpovědný">
            <Select
              className="max-w-72"
              value={odpovednyUserId}
              onChange={(e) => setOdpovednyUserId(e.target.value)}
            >
              <option value="">— nevybráno —</option>
              {uzivatele
                .filter((u) => u.aktivni)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.jmeno}
                  </option>
                ))}
            </Select>
          </Pole>
        )}
        {zalozit.error ? <Chyba>{hlaskaZChyby(zalozit.error)}</Chyba> : null}
        {/* Jedna výrazná primární akce s konkrétním textem; Zrušit tišší. */}
        <div className="flex gap-3">
          <Btn type="submit" disabled={zalozit.isPending}>
            {zalozit.isPending ? 'Zakládám…' : 'Založit zakázku'}
          </Btn>
          <Btn varianta="subtle" onClick={onHotovo}>
            Zrušit
          </Btn>
        </div>
      </form>
    </PoleKarta>
  );
}
